/**
 * Nucleic acid quantitation from A260, and ng ↔ pmol conversion.
 *
 * Concentration: c = A × ε ÷ b, where ε is the nucleic-acid-specific conversion factor in
 * ng·cm/µL (Thermo Fisher NanoDrop: dsDNA 50, ssDNA 33, RNA 40) and b is the path length
 * in cm. Because 1 ng/µL = 1 µg/mL, the factors are also "µg/mL per A260 unit".
 *
 * Moles: average molecular weights per base pair or nucleotide of 660 (dsDNA), 330
 * (ssDNA, oligo) and 340 (RNA) g/mol — the convention behind "1 µg of 1,000 bp DNA =
 * 1.52 pmol". Sources differ by a percent or two; the page says so.
 */

export type NucleicKind = "dsdna" | "ssdna" | "rna" | "oligo";

export const NUCLEIC_KINDS: readonly NucleicKind[] = [
  "dsdna",
  "ssdna",
  "rna",
  "oligo",
];

/** ng·cm/µL (equivalently µg/mL per absorbance unit at 10 mm). */
export const A260_FACTOR: Record<NucleicKind, number> = {
  dsdna: 50,
  ssdna: 33,
  rna: 40,
  oligo: 33,
};

/** Average g/mol per base pair (dsDNA) or per nucleotide (the rest). */
export const G_PER_MOL_PER_UNIT: Record<NucleicKind, number> = {
  dsdna: 660,
  ssdna: 330,
  rna: 340,
  oligo: 330,
};

/** The stated path length every NanoDrop-style value is normalised to. */
export const STANDARD_PATH_MM = 10;

const NG_PER_UG = 1000;
const PMOL_PER_FMOL = 0.001;
/** pmol = ng × 1000 ÷ (g/mol): 1 ng ÷ 1 g/mol = 10⁻⁹ mol = 10³ pmol. */
const PMOL_PER_NG_PER_MW = 1000;

export type AbsorbanceError =
  | "path"
  | "dilution"
  | "factor"
  | "background"
  | "nonPositive";

export interface AbsorbanceInput {
  kind: NucleicKind;
  a260: number;
  a280: number | null;
  a230: number | null;
  /** Turbidity / background reading (A320 or A340) subtracted from every wavelength. */
  background: number | null;
  pathMm: number;
  dilution: number;
  /** Replaces the kind's default factor (ng·cm/µL). */
  factorOverride: number | null;
  volumeUl: number | null;
  /** bp for dsDNA, nt for the rest. */
  length: number | null;
}

export interface AbsorbanceResult {
  a260: number;
  a280: number | null;
  a230: number | null;
  factor: number;
  /** Concentration of the original sample, ng/µL (= µg/mL). */
  concentration: number;
  /** Concentration as read, before the dilution factor. */
  measured: number;
  ratio280: number | null;
  ratio230: number | null;
  totalUg: number | null;
  pmolPerUl: number | null;
  totalPmol: number | null;
}

function correct(value: number | null, background: number): number | null {
  return value === null ? null : value - background;
}

function ratio(top: number, bottom: number | null): number | null {
  return bottom !== null && bottom > 0 ? top / bottom : null;
}

/** Concentration, purity ratios and yield from the readings; an error code when unusable. */
export function fromAbsorbance(
  input: AbsorbanceInput
): { result: AbsorbanceResult } | { error: AbsorbanceError } {
  if (!(input.pathMm > 0)) {
    return { error: "path" };
  }
  if (!(input.dilution > 0)) {
    return { error: "dilution" };
  }
  if (input.factorOverride !== null && !(input.factorOverride > 0)) {
    return { error: "factor" };
  }
  const background = input.background ?? 0;
  if (background < 0 || background >= input.a260) {
    return { error: input.a260 > 0 ? "background" : "nonPositive" };
  }
  const a260 = input.a260 - background;
  const a280 = correct(input.a280, background);
  const a230 = correct(input.a230, background);
  const factor = input.factorOverride ?? A260_FACTOR[input.kind];
  const measured = a260 * factor * (STANDARD_PATH_MM / input.pathMm);
  const concentration = measured * input.dilution;
  const molecular =
    input.length !== null && input.length > 0
      ? G_PER_MOL_PER_UNIT[input.kind] * input.length
      : null;
  const pmolPerUl =
    molecular === null
      ? null
      : (concentration * PMOL_PER_NG_PER_MW) / molecular;
  return {
    result: {
      a260,
      a280,
      a230,
      factor,
      concentration,
      measured,
      ratio280: ratio(a260, a280),
      ratio230: ratio(a260, a230),
      totalUg:
        input.volumeUl !== null && input.volumeUl > 0
          ? (concentration * input.volumeUl) / NG_PER_UG
          : null,
      pmolPerUl,
      totalPmol:
        pmolPerUl !== null && input.volumeUl !== null && input.volumeUl > 0
          ? pmolPerUl * input.volumeUl
          : null,
    },
  };
}

export type RatioVerdict = "low" | "ok" | "high";

const RATIO_280_RANGE: Record<NucleicKind, readonly [number, number]> = {
  dsdna: [1.7, 2.0],
  ssdna: [1.7, 2.0],
  oligo: [1.7, 2.0],
  rna: [1.9, 2.2],
};
const RATIO_230_RANGE = [1.8, 2.4] as const;

function verdict(
  value: number,
  range: readonly [number, number]
): RatioVerdict {
  if (value < range[0]) {
    return "low";
  }
  return value > range[1] ? "high" : "ok";
}

/** A260/A280 against the usual range for the kind of nucleic acid. */
export function judgeRatio280(value: number, kind: NucleicKind): RatioVerdict {
  return verdict(value, RATIO_280_RANGE[kind]);
}

/** A260/A230 against the usual 1.8–2.4 range. */
export function judgeRatio230(value: number): RatioVerdict {
  return verdict(value, RATIO_230_RANGE);
}

export type MassUnit = "ng" | "ug" | "pmol" | "fmol";

export interface Amounts {
  ng: number;
  ug: number;
  pmol: number;
  fmol: number;
  /** pmol of DNA ends: twice the pmol for double-stranded DNA, otherwise the same. */
  pmolEnds: number;
}

/** Convert an amount between ng, µg, pmol and fmol for a given length; null without one. */
export function convertAmount(
  amount: number,
  unit: MassUnit,
  kind: NucleicKind,
  length: number
): Amounts | null {
  if (!(length > 0 && amount >= 0)) {
    return null;
  }
  const molecular = G_PER_MOL_PER_UNIT[kind] * length;
  let ng = amount;
  if (unit === "ug") {
    ng = amount * NG_PER_UG;
  } else if (unit === "pmol") {
    ng = (amount * molecular) / PMOL_PER_NG_PER_MW;
  } else if (unit === "fmol") {
    ng = (amount * PMOL_PER_FMOL * molecular) / PMOL_PER_NG_PER_MW;
  }
  const pmol = (ng * PMOL_PER_NG_PER_MW) / molecular;
  return {
    ng,
    ug: ng / NG_PER_UG,
    pmol,
    fmol: pmol / PMOL_PER_FMOL,
    pmolEnds: kind === "dsdna" ? pmol * 2 : pmol,
  };
}
